import { OnboardingPayload, resolveOnboarding } from './onboarding';
import {
    CoachSettlingPayload,
    resolveCenterAverages,
    resolveCoachSettling,
    sortByNeed,
} from './coachSettling';
import { SetupTaskSlot } from './briefingComposition';
import { OnboardingStepKey } from './onboarding';

/**
 * 단계별 **대상의 단위** — 「강사」인가 「회원」인가 (2026-09-01 QA34).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 필요해졌나
 * ─────────────────────────────────────────────────────────────
 * 카드에서 설명 문장을 뺐다(대표 지시 — 제목과 같은 말을 두 번 했다).
 * 그런데 그 문장 하나가 **숫자의 단위**를 지고 있었다.
 *
 *   「회원 배정」 + 「3명 남음」  → 회원 셋으로 읽힌다
 *   실제 뜻은 「담당 회원이 없는 **강사** 3명」이다
 *
 * 문장을 지우면서 이 사실까지 지우면 숫자가 거짓말을 한다. 그래서 문장이
 * 아니라 **단위**로 남긴다 — 「강사 3명 남음」. 문장보다 짧고 뜻은 같다.
 *
 * ⚠️ 축을 추가하면 여기도 채워라. 안 채우면 단위 없이 「3명」만 남는다.
 */
const ONBOARDING_UNIT: Record<OnboardingStepKey, string> = {
    ADD_COACH: '강사',
    /* 수락을 안 한 것은 강사다 */
    CONNECT_COACH: '강사',
    /* 🔴 담당 회원이 없는 **강사** 수다. 회원 수가 아니다 */
    ASSIGN_MEMBER: '강사',
    INVITE_APP: '회원',
    ADD_MEMBERSHIP: '회원',
    ADD_BOOKING: '회원',
    COACH_HABIT: '강사',
};

/*
 * ─────────────────────────────────────────────────────────────
 * 🔴 「강사 현황에서 보기」 버튼은 **없앴다** (2026-09-01 QA34, 대표 확인)
 * ─────────────────────────────────────────────────────────────
 * QA25 때는 「실재하는 라우트만 건다」는 기준으로 세 단계에 링크를 걸었다.
 * 라우트는 실재했지만, 대표가 눌러 보니 **볼 게 없었다.**
 *
 * 이 카드가 뜨는 센터는 세팅이 안 끝난 신규 센터다. 그런 센터는 강사 현황·
 * 회원 현황 **탭도 비어 있다** — 브리핑 항목이 쌓이지 않았기 때문이다.
 * 「보러 가기」를 눌렀는데 빈 화면이 나오면, 원장님은 그 과제를 할 수 없는
 * 것으로 읽는다. 링크가 없는 것보다 나쁘다.
 *
 * 대상이 누구인지는 이제 **이름 칩**이 카드 안에서 바로 말한다. 굳이 다른
 * 탭으로 보낼 이유 자체가 없어졌다.
 *
 * ⚠️ 되살리려면 **그 탭이 신규 센터에서 뭘 보여주는지 먼저 확인해라.**
 *    라우트가 실재하는지가 아니라 내용이 실재하는지가 기준이다.
 */
/**
 * 세팅·정착 과제를 **오늘의 브리핑 카드로** 뽑는다 (2026-08-28 대표 지시).
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 별도 섹션이 아니라 다섯 건 안인가
 * ─────────────────────────────────────────────────────────────
 * 세팅이 안 끝난 센터에서 그날 원장님이 할 **가장 중요한 일**은 「회원 앱 설치
 * 151명」 같은 과제입니다. 그런데 지금은 그게 아래 별도 섹션에 있고, 위의
 * 다섯 건에는 회원 항목만 올라옵니다 — 정작 급한 일이 접혀 있는 셈입니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 화면에 「신규 센터」·「과도기」라고 쓰지 않습니다 (결정 ④)
 * ─────────────────────────────────────────────────────────────
 * 분류는 **내부 판정 전용**입니다. 원장님에게 「당신 센터는 신규입니다」라고
 * 적으면 등급을 매기는 말이 되고, 그건 이 제품이 할 말이 아닙니다.
 * 화면에는 **할 일만** 적습니다.
 *
 * ⚠️ 데이터가 없으면(404·구서버) **카드를 만들지 않습니다.** 두 응답 모두
 *    실패를 null 로 삼키므로(api/opsBriefing.optional) 여기서는 null 을
 *    「그런 센터가 아니다」가 아니라 **「모른다」** 로 다루고 조용히 빠집니다.
 *    모르는 것을 근거로 과제 카드를 만들면 이미 졸업한 센터에 「세팅하세요」가 뜹니다.
 */

/**
 * 오늘 세울 과제 카드.
 *
 * 순서가 곧 우선순위입니다 — 세팅이 먼저입니다. 강사가 정착하려면 회원과
 * 수강권이 있어야 하는데, 그건 세팅이 끝나야 생깁니다.
 * 자리 상한(최대 2장)과 회원 하한은 `composeBriefing` 이 지킵니다.
 */
export const resolveSetupTasks = (
    onboarding: OnboardingPayload | null | undefined,
    settling: CoachSettlingPayload | null | undefined,
    today: Date = new Date()
): SetupTaskSlot[] => {
    const out: SetupTaskSlot[] = [];

    /*
     * ① 센터 세팅 — **졸업하지 않은 센터만.**
     *
     * 판정은 `onboarding.resolveOnboarding` 한 곳에서만 합니다. 여기서 다시
     * 세면 별도 섹션의 「3/5」와 이 카드가 다른 말을 하게 됩니다.
     *
     * `current` 는 「가장 앞선 미완료」입니다 — 못 잰 항목(UNKNOWN)과 문턱이
     * 없는 항목(REFERENCE)은 여기 안 올라옵니다. 원장님이 할 수 있는 게 없는데
     * 시키는 꼴이 되기 때문입니다. 그래서 `current` 가 없으면 카드도 없습니다.
     */
    if (onboarding) {
        const state = resolveOnboarding(onboarding, today);
        /*
         * 🔴 **졸업한 센터는 과제 카드가 하나도 없다** (2026-08-31 QA11 규칙 ③).
         *
         * 세팅 카드뿐 아니라 **강사 정착 카드까지** 여기서 끊는다.
         * 이 두 카드의 취지가 「회원 위험 카드가 없는 신규 센터의 대체재」라,
         * 세팅을 마친 센터에는 성립하지 않는다. 강사 정착 상세가 필요해지면
         * 그건 브리핑이 아니라 강사 현황 탭의 몫이다.
         *
         * ⚠️ 온보딩 데이터가 없으면(404·구서버) 졸업 여부를 **알 수 없다.**
         *    그때는 여기서 끊지 않고 아래 정착 판정으로 넘어간다 —
         *    모르는 것을 「졸업했다」로 읽으면 신규 센터의 안내가 사라진다.
         *    어차피 자리 보충 규칙(composeBriefing)이 회원 다섯 건인 센터에서는
         *    한 장도 세우지 않으므로, 이 경로로 과제가 새어 나가지 않는다.
         */
        if (state.finished) return [];
        /*
         * ─────────────────────────────────────────────────────────────
         * 🔴 **미완료 축을 전부 세운다** (2026-09-01 QA29, 대표 지시)
         * ─────────────────────────────────────────────────────────────
         * 예전에는 `state.current`(가장 앞선 미완료) **한 장**만 만들었다.
         * 「오늘 할 일 하나에 집중」이라는 뜻이었지만, 신규 센터에서는 그
         * 한 장이 화면의 전부라 원장님이 **무엇을 더 해야 하는지 볼 수가
         * 없었다** — 강사 연결을 끝내야 비로소 다음 축이 나타났다.
         *
         * 이제 사다리의 TODO 를 전부 카드로 만든다. 순서는 사다리 그대로라
         * 위에서부터 하면 된다(`resolveOnboardingSteps` 가 이미 그 순서다).
         *
         * ⚠️ 못 잰 항목(UNKNOWN)과 문턱 없는 항목(REFERENCE)은 **여전히 뺀다.**
         *    `status === 'TODO'` 만 본다 — 원장님이 할 수 있는 게 없는데
         *    시키는 카드를 만들면 안 된다(`resolveOnboarding.current` 와 같은 규칙).
         *
         * ⚠️ 자리 상한은 여기서 걸지 않는다. 화면이 회원 카드 유무를 보고
         *    정한다(`composeBriefing` 의 `setupLimit`) — 과도기 센터는
         *    예전처럼 두 장까지만 세운다.
         */
        state.steps
            .filter((step) => step.status === 'TODO')
            .forEach((step) => {
                out.push({
                    kind: 'SETUP',
                    id: `onboarding:${step.key}`,
                    task: 'ONBOARDING',
                    title: step.title,
                    detail: step.detail,
                    names: step.names,
                    /* 못 잰 것(null)은 그대로 넘긴다 — 화면이 숫자를 안 그린다 */
                    count: step.remaining,
                    unit: ONBOARDING_UNIT[step.key],
                });
            });
    }

    /*
     * ② 강사 정착 — **막힌 강사가 있을 때만, 한 명만.**
     *
     * 「막혔다」의 선은 센터 평균 × 계수입니다(`coachSettling.stuckLine`).
     * 평균을 안 넘기면 고정선으로 판정해 신규 센터가 억울해지므로 반드시 넘깁니다.
     *
     * 여러 명이 막혀 있어도 한 장만 세웁니다. 이 카드의 목적은 「오늘 한 명을
     * 도와라」이지 명단을 주는 것이 아닙니다 — 명단은 강사 정착 섹션에 있습니다.
     * `sortByNeed` 가 가장 도움이 필요한 순으로 세우므로 맨 앞을 씁니다.
     */
    const coaches = settling?.coaches ?? [];
    if (coaches.length > 0) {
        const avg = resolveCenterAverages(coaches);
        const ranked = sortByNeed(coaches.map((c) => resolveCoachSettling(c, today, avg)));
        /*
         * 🔴 **막힌 강사마다 한 장씩** (2026-09-01 QA29).
         *
         * 예전에는 `ranked` 맨 앞 한 명만 세웠다 — 「오늘 한 명을 도와라」였다.
         * 그런데 신규 센터는 강사 전원이 같은 지점에서 막혀 있는 경우가 흔하고,
         * 그때 한 명만 뜨면 나머지는 화면에 없는 사람이 된다.
         *
         * `sortByNeed` 순서 그대로라 가장 도움이 필요한 강사가 위에 선다.
         * 과도기 센터에서는 어차피 상한(2장)에 걸려 맨 앞만 보인다 —
         * 예전 동작이 그대로 유지된다.
         */
        ranked.forEach((c) => {
            if (!c.blocked) return;
            out.push({
                kind: 'SETUP',
                id: `settling:${c.coachId}`,
                task: 'COACH_SETTLING',
                /*
                 * 제목이 **막힌 지점까지** 말한다 (2026-09-01 QA34).
                 * 예전 제목은 「김한솔 강사 정착 지원」이라 카드만 보고는
                 * 뭘 해야 하는지 알 수 없었고, 그걸 아래 설명문이 지고 있었다.
                 * 설명문을 걷어내는 김에 제목이 그 몫을 가져간다.
                 */
                title: `${c.name} 강사 · ${c.blocked.label}에서 막힘`,
                detail: `${c.blocked.label}에서 막혀 있습니다 · ${c.blocked.remedy}`,
                /* 제목에 없는 것만 남긴다 — 「무엇을 하면 풀리는가」 */
                note: c.blocked.remedy,
                names: [],
                /* 강사 한 명을 도우라는 카드다 — 셀 대상이 아니라 숫자를 안 붙인다 */
                count: null,
            });
        });
    }

    return out;
};
